import Aspera from './aspera';
import { stopTransfer, resumeTransfer, removeTransfer } from '@ibm-aspera/sdk';

// Aspera rejects a resume with this code when the transfer is already running.
// See PLATFORM-4050: "Invalid transfer. Transfer is currently: running."
const TRANSFER_ALREADY_RUNNING_CODE = -32006;

const isAlreadyRunningError = (e) => e?.code === TRANSFER_ALREADY_RUNNING_CODE
	|| e?.debugData?.code === TRANSFER_ALREADY_RUNNING_CODE
	|| e?.error?.code === TRANSFER_ALREADY_RUNNING_CODE;

class Transfer {
	constructor(data) {
		this.id = data.uuid;
		this.fileName = data.title;
		this.percentage = (data.percentage * 100).toFixed(2);
		this.rate = data.calculated_rate_kbps;
		this.status = data.status;
		this.addTime = data.add_time;

		this.direction = data.transfer_spec?.direction;

		this.bytes = {
			total: data.bytes_expected,
			received: data.bytes_written
		};

		this.error = !data.error_code
			? null
			: {
				code: data.error_code,
				desc: data.error_desc
			};

		// These are currently untrustworthy because TransferManager creates a new Transfer record on each update,
		// rather then updating existing fields.
		this.isPausing = false;
		this.isResuming = false;
		this.isCancelling = false;
	}

	get isPaused() {
		return this.status === Aspera.TRANSFER_STATUS.CANCELLED;
	}

	get isDownloading() {
		return this.status === Aspera.TRANSFER_STATUS.INITIATING
			|| this.status === Aspera.TRANSFER_STATUS.RUNNING
			|| this.status === Aspera.TRANSFER_STATUS.WILLRETRY
			|| this.status === Aspera.TRANSFER_STATUS.QUEUED;
	}

	// Cancel means removed, not paused. Aspera treats paused as cancelled.
	get isCancelled() {
		return this.status === Aspera.TRANSFER_STATUS.REMOVED;
	}

	get isFailed() {
		return this.status === Aspera.TRANSFER_STATUS.FAILED;
	}

	get isCompleted() {
		return this.status === Aspera.TRANSFER_STATUS.COMPLETED;
	}

	pause = () => {
		this.isPausing = true;

		return stopTransfer(this.id)
			.then((result) => {
				this.status = Aspera.TRANSFER_STATUS.CANCELLED;
				this.isPausing = false;
				return result;
			})
			.catch((e) => {
				this.isPausing = false;
				throw e;
			});
	};

	resume = () => {
		this.isResuming = true;

		return resumeTransfer(this.id)
			.then((result) => {
				this.status = Aspera.TRANSFER_STATUS.RUNNING;
				this.isResuming = false;
				return result;
			})
			.catch((e) => {
				this.isResuming = false;
				// A resume that fails because the transfer is already running is
				// not a failure — the download is live. Reconcile our state to
				// the ground truth and resolve instead of surfacing an error.
				if( isAlreadyRunningError(e) ) {
					this.status = Aspera.TRANSFER_STATUS.RUNNING;
					return { alreadyRunning: true };
				}
				throw e;
			});
	};

	cancel = () => {
		this.isCancelling = true;

		return removeTransfer(this.id)
			.then((result) => {
				this.status = Aspera.TRANSFER_STATUS.REMOVED;
				this.isCancelling = false;
				return result;
			})
			.catch((e) => {
				this.isCancelling = false;
				throw e;
			});
	};
}

export default Transfer;
